// D4 — Análisis por Cliente del directivo.
//
// Participación de volumen por cliente, composición 6 meses (área apilada) y
// detalle por cliente. SOLO LECTURA.
//
// Margen por cliente = utilidad de LÍNEAS en $ (WS-5 B4b), gateado por
// puede_ver_costos. Tiendas aún no (faltan precios de SKUs solo-tienda). El
// pipeline de negociación sigue diferido a F2 (sin modelo de CRM).

import { useSearchParams } from 'react-router-dom';
import type { DirectivoAnalisisCliente } from 'schemas';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { KpiCard } from '../../components/ui/kpi-card';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useDirectivoAnalisisCliente } from '../../lib/api/queries';
import { cn } from '../../lib/utils';
import { PeriodoNav, primerDiaMesActual, shiftMeses } from './components/PeriodoNav';
import { leerMesParam } from './lib/urlParams';
import { hayPendiente, sumarPendientes, textoPendiente } from './lib/pendiente';

function miles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

// Pesos compactos: $12,3 M / $450 k / $980.
function pesosCortos(n: number): string {
  const abs = Math.abs(n);
  const signo = n < 0 ? '-' : '';
  if (abs >= 1_000_000)
    return `${signo}$${(abs / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} M`;
  if (abs >= 1_000)
    return `${signo}$${(abs / 1_000).toLocaleString('es-CO', { maximumFractionDigits: 0 })} k`;
  return `${signo}$${miles(abs)}`;
}

// Color por cliente: principal (índice 0) en accent, resto en grises.
function colorCliente(index: number): string {
  if (index === 0) return 'var(--color-accent)';
  const grises = [
    'var(--color-fg-tertiary)',
    'var(--color-border-stronger)',
    'var(--color-fg-disabled)',
  ];
  return grises[(index - 1) % grises.length]!;
}

export function D4AnalisisClientePage() {
  // El mes vive en la URL (?mes=YYYY-MM-DD, o YYYY-MM → día 1): refrescar /
  // atrás lo conservan. Ausente o inválido → mes en curso (null).
  const [searchParams, setSearchParams] = useSearchParams();
  const mes = leerMesParam(searchParams.get('mes'));
  const setMes = (m: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (m) next.set('mes', m);
        else next.delete('mes');
        return next;
      },
      { replace: true },
    );
  const { data, isLoading, isError } = useDirectivoAnalisisCliente(mes);

  if (isLoading || !data) {
    return (
      <DirectivoLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner />
        </div>
      </DirectivoLayout>
    );
  }
  if (isError) {
    return (
      <DirectivoLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-crit">
          No se pudo cargar el análisis por cliente.
        </div>
      </DirectivoLayout>
    );
  }

  return (
    <DirectivoLayout>
      <div className="mx-auto flex max-w-[1280px] flex-col gap-5">
        <Header data={data} />
        <Filtros data={data} onMes={setMes} />
        <KpiRow data={data} />
        <Composicion data={data} />
        <Detalle data={data} />
        {data.insight ? <Insight insight={data.insight} /> : null}
      </div>
    </DirectivoLayout>
  );
}

function Header({ data }: { data: DirectivoAnalisisCliente }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-[24px] font-medium leading-tight text-fg">Análisis por Cliente</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          {data.clientes_activos} clientes activos · período: {data.mes.label}
        </p>
      </div>
      <button
        type="button"
        disabled
        title="Exportar — disponible en F1.5"
        className="inline-flex cursor-not-allowed items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium text-fg-secondary opacity-60"
      >
        <Icon name="download" size={14} stroke={2} />
        Exportar
      </button>
    </div>
  );
}

function Filtros({
  data,
  onMes,
}: {
  data: DirectivoAnalisisCliente;
  onMes: (mes: string | null) => void;
}) {
  const isCurrent = data.mes.desde >= primerDiaMesActual();
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 rounded-md border border-border bg-surface-elevated p-3">
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Mes
          </span>
          <PeriodoNav
            label={data.mes.label}
            isCurrent={isCurrent}
            canNext={!isCurrent}
            onPrev={() => onMes(shiftMeses(data.mes.desde, -1))}
            onNext={() => onMes(shiftMeses(data.mes.desde, 1))}
            onReset={() => onMes(null)}
          />
        </div>
        <Filtro label="Cliente" valor={`Todos (${data.clientes_activos})`} />
      </div>
      <span className="text-xs text-fg-tertiary">
        Pipeline de negociación · <span className="italic">no disponible (F2)</span>
      </span>
    </div>
  );
}

function Filtro({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className="text-xs text-fg">{valor}</span>
    </div>
  );
}

function KpiRow({ data }: { data: DirectivoAnalisisCliente }) {
  // 3 clientes top + card de clientes nuevos.
  const kpis = data.clientes_kpi.slice(0, 3);
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border lg:grid-cols-4">
      {kpis.map((c) => (
        <div key={c.cliente_id} className="bg-surface-elevated py-4">
          <KpiCard
            label={c.nombre}
            value={c.share_pct}
            valueSuffix="% vol."
            sub={`${miles(c.cantidad)} paq mes`}
          />
        </div>
      ))}
      {/* Relleno si hay menos de 3 clientes */}
      {Array.from({ length: Math.max(0, 3 - kpis.length) }).map((_, i) => (
        <div key={`fill-${i}`} className="bg-surface-elevated py-4">
          <KpiCard label="—" value="—" sub="Sin clientes" />
        </div>
      ))}
      <div className="bg-surface-elevated py-4">
        <KpiCard
          label="Clientes nuevos"
          value={data.clientes_nuevos}
          sub={
            data.pipeline_count === null
              ? 'pipeline no disponible (F2)'
              : `pipeline: ${data.pipeline_count} en negociación`
          }
        />
      </div>
    </div>
  );
}

function Composicion({ data }: { data: DirectivoAnalisisCliente }) {
  // Pivot: una fila por mes, una columna por cliente.
  const chartData = data.composicion.meses.map((m) => {
    const row: Record<string, number | string> = { mes: m.label };
    for (const c of data.composicion.clientes) {
      const punto = data.composicion.puntos.find(
        (p) => p.mes_iso === m.mes_iso && p.cliente_id === c.cliente_id,
      );
      row[c.cliente_id] = punto ? punto.cantidad : 0;
    }
    return row;
  });

  return (
    <Card className="p-4">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-fg">
          Composición del volumen por cliente · 6 meses
        </h2>
        <div className="flex items-center gap-3 text-[11px] text-fg-tertiary">
          {data.composicion.clientes.map((c, i) => (
            <span key={c.cliente_id} className="flex items-center gap-1.5">
              <span className="size-2 rounded-sm" style={{ background: colorCliente(i) }} />
              {c.nombre}
            </span>
          ))}
        </div>
      </div>
      <p className="mb-3 text-xs text-fg-tertiary">Producción mensual apilada por cliente</p>
      <div className="h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="mes"
              tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v) => miles(Number(v))}
              tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(v, _n, item) => [
                `${miles(Number(v))} paq`,
                data.composicion.clientes.find((c) => c.cliente_id === item?.dataKey)?.nombre ?? '',
              ]}
            />
            {data.composicion.clientes.map((c, i) => (
              <Area
                key={c.cliente_id}
                type="monotone"
                dataKey={c.cliente_id}
                name={c.nombre}
                stackId="vol"
                stroke={colorCliente(i)}
                fill={colorCliente(i)}
                fillOpacity={i === 0 ? 0.55 : 0.3}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function Detalle({ data }: { data: DirectivoAnalisisCliente }) {
  // DN10: el margen por cliente va sobre producción VALIDADA; lo capturado sin
  // validar del mes se declara aquí (y por cliente en el tooltip) y no suma.
  const pend = data.pendiente_validacion_utilidad ?? null;
  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-[13px] font-medium text-fg">Detalle por cliente</h2>
        {data.puede_ver_costos ? (
          <p
            className={cn('text-[11px]', hayPendiente(pend) ? 'text-warn' : 'text-fg-tertiary')}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            Margen sobre producción validada ·{' '}
            {textoPendiente(pend, pesosCortos(pend?.valor_producido ?? 0))}
          </p>
        ) : null}
      </div>
      <Table>
        <THead>
          <TR>
            <TH>Cliente</TH>
            <TH className="text-right">Volumen</TH>
            <TH className="text-right">% total</TH>
            <TH>Plantas que sirven</TH>
            <TH className="text-right">Cumpl.</TH>
            <TH className="text-right" title="Utilidad ÷ valor producido sobre producción validada">
              Margen · validado
            </TH>
          </TR>
        </THead>
        <TBody>
          {data.detalle.map((c, i) => (
            <TR key={c.cliente_id}>
              <TD>
                <span className="flex items-center gap-1.5 font-medium text-fg">
                  <span className="size-2 rounded-sm" style={{ background: colorCliente(i) }} />
                  {c.nombre}
                </span>
                <span className="text-[11px] text-fg-tertiary">{c.skus_count} SKUs</span>
              </TD>
              <TD className="text-right tabular-nums text-fg">{miles(c.volumen)}</TD>
              <TD className="text-right">
                <span className="flex items-center justify-end gap-2">
                  <span className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-overlay">
                    <span
                      className="block h-full rounded-full"
                      style={{
                        width: `${Math.min(100, c.share_pct)}%`,
                        background: colorCliente(i),
                      }}
                    />
                  </span>
                  <span className="tabular-nums text-fg">{c.share_pct}%</span>
                </span>
              </TD>
              <TD className="text-fg-tertiary">{c.plantas.join(', ') || '—'}</TD>
              <TD className="text-right tabular-nums text-fg">
                {c.cumplimiento_pct !== null ? `${c.cumplimiento_pct.toFixed(1)}%` : 's/d'}
              </TD>
              <TD
                className="text-right tabular-nums"
                title={
                  c.utilidad !== null
                    ? `${pesosCortos(c.utilidad)} utilidad · producción validada${
                        c.pendiente_validacion_utilidad &&
                        c.pendiente_validacion_utilidad.paquetes > 0
                          ? ` · pendiente de validar ${miles(c.pendiente_validacion_utilidad.paquetes)} paq · ${pesosCortos(c.pendiente_validacion_utilidad.valor_producido)} (no suma)`
                          : ''
                      }`
                    : undefined
                }
              >
                {!data.puede_ver_costos ? (
                  <span className="text-fg-tertiary">—</span>
                ) : c.margen_pct !== null ? (
                  <span className="text-fg">
                    {c.margen_pct.toFixed(1)}%
                    {c.utilidad !== null ? (
                      <span className="ml-1.5 text-[11px] text-fg-tertiary">
                        {pesosCortos(c.utilidad)}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-fg-tertiary">s/d</span>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}

function Insight({ insight }: { insight: NonNullable<DirectivoAnalisisCliente['insight']> }) {
  return (
    <Card
      variant={insight.tono === 'warn' ? 'crit-left' : 'accent-left'}
      className="flex items-start gap-3 p-4"
    >
      <span
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
          insight.tono === 'warn' ? 'bg-crit-soft text-crit' : 'bg-accent-soft text-accent',
        )}
      >
        <Icon name="info" size={13} stroke={2} />
      </span>
      <div>
        <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-fg-tertiary">
          Insight detectado automáticamente
        </span>
        <p className="mt-0.5 text-sm text-fg">{insight.texto}</p>
      </div>
    </Card>
  );
}
